import memory from "@/.cache/translation-memory.json";
import { DEFAULT_LOCALE } from "./i18n-config";
import { getTranslationKey, isTranslatable, normalizeTranslationText } from "./i18n";

// The committed translation memory, imported directly so it is bundled into the
// build. `npm run build` never calls an AI service — it only reads this file.
// Missing entries fall through to the English text, so an incomplete memory
// degrades to a partly-English page rather than a failed build.

type MemoryEntry = Record<string, string>;
const MEMORY = memory as Record<string, MemoryEntry>;

/**
 * With COLLECT_I18N=1 the build records every string the renderer actually asks
 * for into .cache/i18n-strings.jsonl. Collecting at the lookup point (rather
 * than by scraping HTML or parsing JSX) guarantees the translate script sees
 * exactly the strings, and exactly the keys, the site will look up later.
 */
function recordString(text: string, locale: string, translated: boolean) {
  if (typeof window !== "undefined" || process.env.COLLECT_I18N !== "1") return;
  try {
    // eval keeps webpack from trying to bundle fs into the client build.
    const fs = eval("require")("fs") as typeof import("fs");
    fs.appendFileSync(
      ".cache/i18n-strings.jsonl",
      JSON.stringify({
        key: getTranslationKey(text),
        en: normalizeTranslationText(text),
        locale,
        translated,
      }) + "\n"
    );
  } catch {
    /* collection is best-effort and must never break a build */
  }
}

export function translateText(text: string, locale: string): string {
  if (locale === DEFAULT_LOCALE || !text) return text;
  if (!isTranslatable(text)) return text;

  const hit = MEMORY[getTranslationKey(text)]?.[locale];
  recordString(text, locale, Boolean(hit));
  if (!hit) return text;

  // Preserve the original leading/trailing whitespace: JSX relies on it for
  // spacing between inline elements, and the memory stores trimmed strings.
  const leading = text.match(/^\s*/)?.[0] ?? "";
  const trailing = text.match(/\s*$/)?.[0] ?? "";
  return `${leading}${hit}${trailing}`;
}

export function hasTranslation(text: string, locale: string): boolean {
  if (locale === DEFAULT_LOCALE) return true;
  return Boolean(MEMORY[getTranslationKey(text)]?.[locale]);
}

/** Deep-translates plain data (strings, arrays, objects) for page props. */
export function translateData<T>(value: T, locale: string, skipKeys = new Set<string>()): T {
  if (locale === DEFAULT_LOCALE) return value;

  const walk = (node: any, key?: string): any => {
    if (typeof node === "string") {
      return key && skipKeys.has(key) ? node : translateText(node, locale);
    }
    if (Array.isArray(node)) return node.map((item) => walk(item, key));
    if (node && typeof node === "object") {
      return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, walk(v, k)]));
    }
    return node;
  };

  return walk(value) as T;
}

export { normalizeTranslationText, getTranslationKey };
